/**
 * 全局登录状态 Context,对应产品文档 §10.1(RBAC:前端按角色控制可见操作)。
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getCurrentUser, getIdToken, getUserGroups, logout as cognitoLogout } from "./cognitoClient";

export const ROLE_ADMIN = "Admin";
export const ROLE_OPERATOR = "Operator";
export const ROLE_VIEWER = "Viewer";

interface AuthState {
  loading: boolean;
  isAuthenticated: boolean;
  username: string | null;
  roles: string[];
  idToken: string | null;
  refresh: () => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  canWrite: boolean; // Admin 或 Operator 均可执行建号/退订/升降级等写操作(§10.1)
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [username, setUsername] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    const user = getCurrentUser();
    if (!user) {
      setIdToken(null);
      setRoles([]);
      setUsername(null);
      setLoading(false);
      return;
    }
    const token = await getIdToken();
    setIdToken(token);
    if (token) {
      // getUserGroups 需要 CognitoUserSession,这里通过 getSession 重新拿一次
      // (getIdToken 内部已经调用过一次 getSession,为保持模块边界清晰,这里
      // 单独再取一次 groups,量级很小,不值得为此调整 cognitoClient 的接口)
      user.getSession((err: Error | null, session: any) => {
        if (!err && session) {
          setRoles(getUserGroups(session));
          // User Pool 配置了 signInAliases: { email: true },user.getUsername()
          // 返回的是 Cognito 内部生成的 UUID(真正的 Username),不是登录用的邮箱;
          // 邮箱需要从 ID Token 的 email claim 读取才能展示正确的登录人身份。
          const email = session.getIdToken().decodePayload()?.email as string | undefined;
          setUsername(email ?? user.getUsername());
        }
        setLoading(false);
      });
    } else {
      setUsername(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    cognitoLogout();
    setIdToken(null);
    setRoles([]);
    setUsername(null);
  };

  const value = useMemo<AuthState>(
    () => ({
      loading,
      isAuthenticated: !!idToken,
      username,
      roles,
      idToken,
      refresh,
      logout: handleLogout,
      isAdmin: roles.includes(ROLE_ADMIN),
      canWrite: roles.includes(ROLE_ADMIN) || roles.includes(ROLE_OPERATOR),
    }),
    [loading, idToken, roles, username]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return ctx;
}
